/**
 * The suggestion box - issue #34.
 *
 * A bug report or an idea, filed as a GitLab issue on BEST's own repository
 * without the person having to know GitLab exists. Most people using BEST are
 * modelling, not maintaining the tool, and the feedback that mattered was
 * arriving in conversation and dying there.
 *
 * Two rules the shape follows:
 *
 *  - The button is not drawn when it could not work. `GET /api/feedback` folds
 *    a sandbox, an unconfigured GitLab, an anonymous caller and a read-only
 *    token into one `available`, because they are the same fact to the person
 *    looking at the toolbar. A control that fails on click teaches people the
 *    tool is broken.
 *  - The dialog reports WHERE the issue landed. It says "filed as #41" with a
 *    link out, because an issue you cannot find again is indistinguishable
 *    from one that was never raised - and the whole reason this exists is that
 *    people did not believe their feedback went anywhere.
 *
 * The page is sent along rather than read from the Referer header, which a
 * same-origin SPA does not change per route.
 *
 * It takes a prefill and a shape because of where else it is now drawn: the
 * error screen, which is what somebody sees when BEST cannot read the model at
 * all. That is precisely when a person cannot reach the toolbar to report
 * anything - the last one had to be relayed over Slack and raised by hand -
 * and it is also when the machine knows more about the failure than the
 * reporter does, so the report writes its own first draft.
 */

import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FeedbackIcon from '@mui/icons-material/FeedbackOutlined'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { ApiError, api } from '../../api'
import type { FeedbackTarget, RaisedIssue } from '../../schema'

/** What the two kinds are called before the server has answered. */
const FALLBACK_KINDS = [
  { value: 'bug', label: 'Bug report' },
  { value: 'suggestion', label: 'Suggestion' },
]

/**
 * @param trigger  'icon' for the toolbar, 'button' where it has to be found by
 *                 somebody who is stuck and is not looking for an icon.
 * @param prefill  A first draft. Restored on close rather than cleared, so a
 *                 reporter who closes the dialog by accident does not lose the
 *                 diagnosis that was written for them.
 */
export default function SuggestionBox({
  trigger = 'icon',
  label = 'Report a bug',
  prefill,
}: {
  trigger?: 'icon' | 'button'
  label?: string
  prefill?: { kind?: string; title?: string; detail?: string }
} = {}) {
  const location = useLocation()
  const [target, setTarget] = useState<FeedbackTarget | null>(null)
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState(prefill?.kind ?? 'bug')
  const [title, setTitle] = useState(prefill?.title ?? '')
  const [detail, setDetail] = useState(prefill?.detail ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [raised, setRaised] = useState<RaisedIssue | null>(null)

  // Asked once. Whether feedback can be filed depends on configuration and on
  // the token BEST holds, neither of which changes while the page is open -
  // and a failure here means the button stays hidden, which is the same
  // outcome as an unconfigured install.
  useEffect(() => {
    let live = true
    api
      .feedbackTarget()
      .then((answer) => live && setTarget(answer))
      .catch(() => live && setTarget(null))
    return () => {
      live = false
    }
  }, [])

  if (!target?.available) return null

  const kinds = target.kinds.length ? target.kinds : FALLBACK_KINDS

  const close = () => {
    setOpen(false)
    // Cleared on close rather than on send: the confirmation with the issue
    // number in it is the point, and clearing it as the dialog fades would
    // take the link away before it could be read.
    setError('')
    setRaised(null)
    setTitle(prefill?.title ?? '')
    setDetail(prefill?.detail ?? '')
  }

  const send = async () => {
    setBusy(true)
    setError('')
    try {
      setRaised(
        await api.sendFeedback({
          title: title.trim(),
          detail: detail.trim(),
          kind,
          page: location.pathname,
        }),
      )
    } catch (failure) {
      const refused = failure as ApiError
      setError(refused.message || 'Could not raise the issue.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {trigger === 'button' ? (
        <Button
          variant="outlined"
          startIcon={<FeedbackIcon />}
          onClick={() => setOpen(true)}
        >
          {label}
        </Button>
      ) : (
        <Tooltip title="Report a bug or suggest a change">
          <IconButton
            color="inherit"
            onClick={() => setOpen(true)}
            aria-label="Report a bug or suggest a change"
          >
            <FeedbackIcon />
          </IconButton>
        </Tooltip>
      )}

      <Dialog open={open} onClose={busy ? undefined : close} maxWidth="sm" fullWidth>
        <DialogTitle>{raised ? 'Thank you' : 'Tell us about BEST'}</DialogTitle>
        <DialogContent>
          {raised ? (
            <Alert severity="success">
              Filed as{' '}
              <Link href={raised.web_url} target="_blank" rel="noreferrer">
                issue #{raised.iid}
              </Link>
              . It is on the board under your own name, so you will hear back
              there.
            </Alert>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                This raises an issue on BEST&apos;s own repository, as you - so
                somebody can ask you a follow-up question. It does not touch
                any model.
              </DialogContentText>

              <Stack spacing={2}>
                <TextField
                  select
                  label="Kind"
                  value={kind}
                  onChange={(event) => setKind(event.target.value)}
                  fullWidth
                >
                  {kinds.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="One line"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder={
                    kind === 'bug'
                      ? 'The requirements table cuts off long statements'
                      : 'Let the requirements table wrap'
                  }
                  helperText="This becomes the issue title."
                  fullWidth
                  autoFocus
                />
                <TextField
                  label={kind === 'bug' ? 'What happened' : 'What would be better'}
                  value={detail}
                  onChange={(event) => setDetail(event.target.value)}
                  multiline
                  minRows={4}
                  helperText={
                    kind === 'bug'
                      ? 'What you did, what you expected, and what happened instead.'
                      : 'Optional, but what you were trying to do is the useful part.'
                  }
                  fullWidth
                />
              </Stack>

              {error ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {target.url ? (
            <Link
              href={target.url}
              target="_blank"
              rel="noreferrer"
              variant="body2"
              sx={{ mr: 'auto', ml: 1 }}
            >
              See what has been raised
            </Link>
          ) : null}
          <Button onClick={close} disabled={busy}>
            {raised ? 'Close' : 'Cancel'}
          </Button>
          {raised ? null : (
            <Button
              onClick={() => void send()}
              variant="contained"
              disabled={busy || title.trim() === ''}
            >
              {busy ? 'Sending…' : 'Send'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
